import { TODAY_STATE_OF } from "@swtracker/core/portal/nextStep";
/**
 * The 12-state workflow status system (idc-portal-design v1.1): each state has its own icon (which state), a tone
 * (whose move it is) and a phase (how far). One catalogue drives the pills, the status key, the hero filters and the
 * screen-reader text, so they can't drift. Labels are always full text; there are no numeric codes.
 * AIC-304: a state's tone is the status's usual owner, not the assignment's actual next step: that is tracker-api's
 * next_step (@swtracker/core/jobs/next-step), which the Assignment page's hero and checklist show. They can differ
 * (Synced reads "waiting" while nothing prepares the spec until the analyst asks; Spec approved reads "waiting" when
 * the invite draft wasn't created). The pills keep their tones so every list and filter reads the same.
 */
import { Icon } from "./Sprites.tsx";

export type Tone = "you" | "risk" | "wait" | "done" | "off";
export type Phase = 0 | 1 | 2 | 3 | 4; // 0 = closed
export const PHASES = ["Prepare", "Outreach", "Interview", "Close"] as const;

export const STATES = {
  synced: { label: "Synced", icon: "sync", tone: "wait", phase: 1 },
  specReady: { label: "Spec ready", icon: "doc", tone: "you", phase: 1 },
  specOk: { label: "Spec approved", icon: "doc-ok", tone: "wait", phase: 1 },
  drafted: { label: "Invite drafted", icon: "draft", tone: "you", phase: 2 },
  sent: { label: "Sent", icon: "sent", tone: "wait", phase: 2 },
  sched: { label: "Interview scheduled", icon: "cal", tone: "wait", phase: 2 },
  interviewed: { label: "Interviewed", icon: "mic", tone: "wait", phase: 3 },
  results: { label: "Results ready", icon: "results", tone: "you", phase: 3 },
  follow: { label: "Needs follow-up", icon: "alert", tone: "risk", phase: 3 },
  approved: { label: "Results approved", icon: "ok", tone: "done", phase: 4 },
  exported: { label: "Exported", icon: "db", tone: "done", phase: 4 },
  withdrawn: { label: "Withdrawn", icon: "off", tone: "off", phase: 0 },
} as const satisfies Record<string, { label: string; icon: string; tone: Tone; phase: Phase }>;
export type StateKey = keyof typeof STATES;

/**
 * tracker-api's assignment statuses (db/migrations/0001_init.sql) → the catalogue. "Interview scheduled" and
 * "Needs follow-up" are not assignment statuses: tracker-api derives them from the session and review / result state
 * and sends them as a row's display_status (AIC-181, routes/portal-rows.ts), which the queue and list show.
 */
const FROM_API = TODAY_STATE_OF;

/** The catalogue state for an API status; null for a status the catalogue doesn't know. */
export function stateOf(status: string): StateKey | null {
  return Object.hasOwn(FROM_API, status) ? FROM_API[status]! : null;
}

export const needsYou = (state: StateKey | null): boolean => state !== null && (STATES[state].tone === "you" || STATES[state].tone === "risk");

const humanize = (s: string) => s.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

export function PhaseMeter({ phase }: { readonly phase: Phase }) {
  return (
    <span className="ipd-phase" aria-hidden="true">
      {[1, 2, 3, 4].map((n) => <i key={n} className={n <= phase ? "on" : undefined} />)}
    </span>
  );
}

/**
 * The recorded status as a neutral fact (AIC-310, S1): the icon, label and phase with no tone, so it never reads as
 * "your move" (in colour or to a screen reader). The Assignment page leads with the next step and shows this beside it.
 */
export function StatusFact({ state, fallback }: { readonly state: StateKey | null; readonly fallback?: string }) {
  const s = state ? STATES[state] : null;
  return (
    <span className="ipd-st ipd-st-fact">
      {s ? <PhaseMeter phase={s.phase} /> : null}
      <span className="ipd-pill ipd-pill--fact" data-state={state ?? undefined}>
        <Icon name={s ? s.icon : "info"} />
        <span><span className="ipd-sr">Recorded status: </span>{s ? s.label : humanize(fallback ?? "unknown")}</span>
      </span>
      {s ? <span className="ipd-sr">, {s.phase ? `${PHASES[s.phase - 1]} phase` : "closed"}</span> : null}
    </span>
  );
}
